import { useIntl } from 'react-intl';
import { ChainType, PickingChainEntry } from '../models/PickingChainEntry';

interface PickingChainListProps {
  items: PickingChainEntry[];
  loading: boolean;
  chainType: ChainType | null;
  canEdit: boolean;
  onEdit: (entry: PickingChainEntry) => void;
  onDelete: (id: number) => void;
}

export function PickingChainList({
  items,
  loading,
  chainType,
  canEdit,
  onEdit,
  onDelete,
}: PickingChainListProps) {
  const intl = useIntl();
  const showZoneCode = chainType === 'bvs' || !chainType;

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-10">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">
            {intl.formatMessage({ id: 'common.loading' })}
          </span>
        </div>
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <div className="py-10 text-muted">
        {intl.formatMessage({ id: 'pickingChain.noResults' })}
      </div>
    );
  }

  return (
    <div className="table-responsive">
      <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
        <thead>
          <tr className="fw-bold text-muted">
            {!chainType && (
              <th>{intl.formatMessage({ id: 'pickingChain.chainType' })}</th>
            )}
            <th>{intl.formatMessage({ id: 'pickingChain.position' })}</th>
            <th>{intl.formatMessage({ id: 'pickingChain.ean' })}</th>
            <th>{intl.formatMessage({ id: 'pickingChain.productName' })}</th>
            {showZoneCode && (
              <th>{intl.formatMessage({ id: 'pickingChain.zoneCode' })}</th>
            )}
            {canEdit && (
              <th>{intl.formatMessage({ id: 'pickingChain.actions' })}</th>
            )}
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id}>
              {!chainType && (
                <td>
                  <span
                    className={`badge ${
                      item.chain_type === 'main'
                        ? 'badge-light-primary'
                        : 'badge-light-info'
                    }`}
                  >
                    {item.chain_type === 'main'
                      ? intl.formatMessage({ id: 'pickingChain.main' })
                      : intl.formatMessage({ id: 'pickingChain.bvs' })}
                  </span>
                </td>
              )}
              <td>
                <span className="text-dark fw-bold">{item.position}</span>
              </td>
              <td>{item.ean}</td>
              <td>{item.product_name}</td>
              {showZoneCode && (
                <td>
                  {item.chain_type === 'bvs' ? item.zone_code || '-' : ''}
                </td>
              )}
              {canEdit && (
                <td>
                  <button
                    type="button"
                    className="btn btn-sm btn-light-primary me-2"
                    onClick={() => onEdit(item)}
                  >
                    <i className="fa fa-pencil-alt" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-light-danger"
                    onClick={() => {
                      if (
                        window.confirm(
                          intl.formatMessage({
                            id: 'pickingChain.deleteConfirm',
                          })
                        )
                      ) {
                        onDelete(item.id);
                      }
                    }}
                  >
                    <i className="fa fa-trash" />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
